export { showError } from '../../../../utils';

export function clsx(...args) {
  return args
    .flatMap((arg) => {
      if (!arg) return []; // skip falsy values

      if (typeof arg === 'string') {
        return [arg]; // simple string class
      }

      if (Array.isArray(arg)) {
        return arg.filter(Boolean); // handle arrays: ["a", false, "b"]
      }

      if (typeof arg === 'object') {
        return Object.keys(arg).filter((key) => arg[key]);
        // handle objects: { active: true, disabled: false }
      }

      return [];
    })
    .join(' ');
}

export function formatDateWithOrdinal(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
          ? 'rd'
          : 'th';
  const month = date.toLocaleString('default', { month: 'long' });
  return `${day}${suffix} ${month}`;
}

export function formatDate(inputDate) {
  if (!inputDate) return '';

  let date;

  // Handle DD-MM-YY or DD-MM-YYYY
  if (typeof inputDate === 'string' && inputDate.includes('-')) {
    const [dd, mm, yy] = inputDate.split('-');
    const year = yy.length === 2 ? `20${yy}` : yy; // 25 → 2025

    date = new Date(year, mm - 1, dd);
  } else {
    date = new Date(inputDate);
  }

  if (isNaN(date)) return '';

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatINR(amount) {
  const number = Number(amount);
  if (isNaN(number)) return '₹0';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
}

/* ================= Slot / date helpers ================= */

export const formatSlotLabel = (slot) => {
  const { startTime, endTime } = slot;
  if (startTime == null || endTime == null) return slot.label;

  const formatHour = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const h = hour % 12 || 12;
    return `${h}:00 ${period}`;
  };

  return `${formatHour(startTime)} - ${formatHour(endTime)}`;
};

export const getTabForCurrentTime = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Morning';
  if (hour < 17) return 'Afternoon';
  return 'Evening';
};

export const getDaysDiffFromToday = (dateStr) => {
  const today = new Date();
  const targetDate = new Date(dateStr);

  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  return Math.round(
    (targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
};

export const getFirstTabWithEnabledSlots = (slots, dateStr) => {
  const tabs = ['Morning', 'Afternoon', 'Evening'];
  const morning = [];
  const afternoon = [];
  const evening = [];

  slots.forEach((slot) => {
    if (!slot.enabled) return;
    const label = slot.label.toLowerCase();
    const hour = parseInt(label.split(':')[0], 10);
    if (label.includes('am')) {
      morning.push(slot);
    } else if (hour === 12 || hour < 5) {
      afternoon.push(slot);
    } else {
      evening.push(slot);
    }
  });

  const buckets = { Morning: morning, Afternoon: afternoon, Evening: evening };
  const isToday = dateStr ? getDaysDiffFromToday(dateStr) === 0 : false;
  const startTab = isToday ? getTabForCurrentTime() : 'Morning';
  const startIdx = tabs.indexOf(startTab);

  // Try starting tab first, then wrap around
  for (let i = 0; i < tabs.length; i++) {
    const tab = tabs[(startIdx + i) % tabs.length];
    if (buckets[tab].length > 0) return tab;
  }
  return 'Morning';
};

export const isDateEnabled = (dateObj) =>
  dateObj?.enabled !== false &&
  Array.isArray(dateObj?.time) &&
  dateObj.time.some((slot) => slot.enabled !== false);

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

export const getCurrentLocation = (setLocationDenied) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          long: position.coords.longitude,
        });
      },
      (error) => {
        if (setLocationDenied && setLocationDenied instanceof Function) {
          setLocationDenied(true);
        }
        reject(error);
      },
      // City/pincode-level accuracy is enough for lab-test address selection,
      // so skip the slow GPS fix: enableHighAccuracy:false uses the fast
      // network/coarse provider, maximumAge reuses a recent fix (up to 5 min)
      // instead of re-acquiring, and a shorter timeout fails fast.
      { enableHighAccuracy: false, timeout: 25000, maximumAge: 300000 }
    );
  });
};
